/**
 * Practido Electronics Fun Kit - Complete Project Manual Data
 * Source: Official Project Manual (Oct 26, 2026 by @Shilpi)
 */

export const kitMeta = {
  title: "Practido Electronics Fun Kit – Project Manual",
  subtitle: "Build 15 fun projects with your kit and simple things from home. No soldering. Ask a grown-up to help with scissors and pins.",
  author: "Shilpi",
  authorHandle: "@Shilpi",
  date: "Oct 26, 2026",
  supportPhone: "9560734660",
  supportWhatsAppLink: "https://wa.me/919560734660?text=Hi%20Practido,%20I%20have%20a%20question%20about%20the%20Electronics%20Fun%20Kit",
  websiteUrl: "https://www.practido.com",
};

export const kitComponents = [
  {
    id: "battery-9v",
    name: "9V Battery",
    category: "power",
    whatItDoes: "Gives the power for your circuits.",
    icon: "battery-charging",
    color: "#246CFF",
    badge: "Power Source",
  },
  {
    id: "battery-connector",
    name: "Battery Connector",
    category: "power",
    whatItDoes: "Clips onto the 9V battery. Red wire is plus (+). Black wire is minus (−).",
    icon: "plug-zap",
    color: "#FF8E1F",
    badge: "Wires",
  },
  {
    id: "leds",
    name: "LEDs (Red, Yellow, Green)",
    category: "output",
    whatItDoes: "Tiny lights. They work only one way round (long leg = +, short leg = −).",
    icon: "lightbulb",
    color: "#3DC1CA",
    badge: "Lights",
  },
  {
    id: "resistors",
    name: "Resistors (Small Striped Parts)",
    category: "safety",
    whatItDoes: "Keep the LED safe from burning. Always use one with an LED and the 9V battery.",
    icon: "shield-check",
    color: "#9E2BD1",
    badge: "Protector",
  },
  {
    id: "buzzer",
    name: "Buzzer",
    category: "output",
    whatItDoes: "Makes a loud, cheerful beep when electric current passes through.",
    icon: "volume-2",
    color: "#FF5A5F",
    badge: "Sound",
  },
  {
    id: "push-button",
    name: "Push Button",
    category: "input",
    whatItDoes: "Momentary switch: Press = ON, Let go = OFF.",
    icon: "toggle-right",
    color: "#246CFF",
    badge: "Switch",
  },
  {
    id: "motor",
    name: "DC Toy Motor",
    category: "motion",
    whatItDoes: "Spins round and round. Reverse the wires to spin the other way!",
    icon: "cog",
    color: "#FF8E1F",
    badge: "Motion",
  },
  {
    id: "fan-blade",
    name: "Fan Blade",
    category: "motion",
    whatItDoes: "Fits snugly onto the motor shaft to create breeze and movement.",
    icon: "fan",
    color: "#3DC1CA",
    badge: "Accessory",
  },
  {
    id: "rocket-card",
    name: "Rocket Card",
    category: "craft",
    whatItDoes: "Your very first paper-circuit project card ready to fold and light up.",
    icon: "rocket",
    color: "#9E2BD1",
    badge: "Project 1",
  },
];

export const householdItems = [
  { name: "Tape (Sellotape or Electrical Tape)", icon: "tape" },
  { name: "Kitchen Aluminum Foil", icon: "sparkles" },
  { name: "Glue Stick", icon: "stick" },
  { name: "Scissors", icon: "scissors" },
  { name: "Paper Cups", icon: "cup" },
  { name: "Cardboard Scraps", icon: "box" },
  { name: "Paper Clips", icon: "paperclip" },
  { name: "Sketch Pens / Markers", icon: "pen-tool" },
  { name: "Clay / Plasticine", icon: "circle" },
  { name: "Wooden Clothes Peg", icon: "anchor" },
  { name: "Thread / String", icon: "activity" },
  { name: "Drawing Pin / Safety Pin", icon: "pin" },
];

export const safetyRules = [
  {
    id: 1,
    title: "Never Let Red & Black Touch Directly",
    desc: "A direct connection creates a short circuit. Keep the wire tips separated until connected through a part.",
    icon: "alert-triangle",
    badge: "Critical",
  },
  {
    id: 2,
    title: "Always Use a Resistor with an LED on 9V",
    desc: "The 9V battery has too much power for tiny LEDs alone. A resistor protects the LED so it doesn't burn out.",
    icon: "shield-alert",
    badge: "Protection",
  },
  {
    id: 3,
    title: "Unclip the Battery When Finished",
    desc: "Always disconnect the battery when you're done playing. If any part ever feels warm, unclip it straight away.",
    icon: "power",
    badge: "Best Practice",
  },
  {
    id: 4,
    title: "Keep Away from Little Children & Mouths",
    desc: "Batteries, resistors, and small components are choking hazards. Keep them out of reach of infants and toddlers.",
    icon: "baby",
    badge: "Child Safety",
  },
];

export const goldenBasics = [
  {
    id: "rule-1",
    ruleNumber: 1,
    title: "LED Polarity: Long Leg vs Short Leg",
    summary: "Long leg is PLUS (+). Short leg is MINUS (−). Wrong way round = no light.",
    details:
      "LEDs only let electricity flow in one direction. The LONG leg must always connect towards the RED (plus) wire, and the SHORT leg connects towards the BLACK (minus) wire.",
    badge: "LED Rule",
    diagramType: "led",
  },
  {
    id: "rule-2",
    ruleNumber: 2,
    title: "Push Button: Opposite Diagonal Corners",
    summary: "Use two legs at opposite corners (one top, one bottom, on different sides).",
    details:
      "The button has 4 legs connected in pairs inside. By picking opposite diagonal corners, your circuit will work reliably every single time you press it.",
    badge: "Button Rule",
    diagramType: "button",
  },
  {
    id: "rule-3",
    ruleNumber: 3,
    title: "Twist & Tape: No Soldering Needed",
    summary: "Cross the metal ends, twist 3–4 times tightly, then tape firmly. Metal must touch metal.",
    details:
      "Tape holds the joint steady, but electricity only flows where metal touches bare metal. Make sure stripped wires wrap snugly around component leads before taping.",
    badge: "Joint Rule",
    diagramType: "twist",
  },
];

export const projectFunFacts = {
  1: "Paper circuits are used by NASA space engineers to design ultralight flexible sensors for small cube satellites! 🛰️",
  2: "The first visible LED was invented in 1962 by Nick Holonyak. Today, LEDs save over 80% more energy than incandescent bulbs! 💡",
  3: "The push-button switch was invented in the 1880s! Today, a modern computer keyboard has over 100 push buttons inside! ⌨️",
  4: "Piezoelectric buzzers contain a tiny crystal disc that expands and contracts thousands of times every second to vibrate air into sound! 🐝",
  5: "The first traffic signal was installed outside the Houses of Parliament in London in 1868—years before cars were even invented! 🚦",
  6: "Electric motors work using electromagnetism: when electricity flows through coiled wire, it creates a magnetic field that pushes against magnets to spin! 🔄",
  7: "In 1844, Samuel Morse sent the very first telegraph message over 40 miles: 'What hath God wrought!' Today, it's used by aviators and ships! 📡",
  8: "Astronauts and neurosurgeons practice steady-hand muscle control using electronic wire-loop simulators just like this game! 👨‍🚀",
  9: "Raindrops conduct electricity because they pick up tiny dissolved salt and mineral ions from the atmosphere on their fall down! 🌧️",
  10: "Pressure-sensitive floor mat switches like this welcome fan are used in airport security and automatic sliding supermarket doors! 🚪",
  11: "Silver, copper, and gold are the top 3 conductors in the universe! Graphite in pencils is one of the only non-metals that conducts electricity! ⚡",
  12: "A pencil dimmer is a real variable resistor (potentiometer)! Volume knobs on guitars and car stereos work on this exact same principle! 🎸",
  13: "In 1666, legendary scientist Isaac Newton placed a prism in sunlight and proved that white light is secretly a rainbow of colors blended together! 🌈",
  14: "Doodling robots use an off-center counterweight (vibrobotics). When the motor spins an unbalanced weight, it wobbles in wild artistic loops! 🤖",
  15: "Tiny vibrating motors with eccentric weights are hiding inside every smartphone and video game controller to create rumble effects! 🎮",
};

export const projectsList = [
  {
    id: 1,
    number: "01",
    title: "Rocket Card",
    time: "15 min",
    difficulty: "Beginner",
    category: "Lights",
    tagline: "Light up the rocket on your card!",
    description:
      "Build your first paper circuit! Use kitchen foil strips as conductive tracks to light up the rocket window using a coin battery (CR2032) or the 9V battery with a resistor.",
    kitNeeds: ["Rocket card", "LED (Red)", "Resistor (if using 9V)"],
    homeNeeds: ["Kitchen foil", "Glue stick", "Tape", "Scissors", "Coin battery (CR2032) or 9V battery"],
    youNeedImage: "/img/electronics-kit/p1_rocket_card_you_need.png",
    tryItImage: "/img/electronics-kit/p1_rocket_card_try_it.png",
    noBatteryImage: "/img/electronics-kit/p1_rocket_card_no_battery.png",
    steps: [
      {
        stepNumber: 1,
        title: "Cut & Glue Foil Tracks",
        instruction:
          "Cut thin foil strips. Glue them on the orange lines. Fold the foil at corners. The + and − lines must never touch.",
        image: "/img/electronics-kit/p1_rocket_card_step1.png",
      },
      {
        stepNumber: 2,
        title: "Insert & Tape the LED",
        instruction:
          "Poke a small hole at the X. Push the LED through. Long leg on the + line, short leg on the − line. Tape the legs.",
        image: "/img/electronics-kit/p1_rocket_card_step2.png",
      },
      {
        stepNumber: 3,
        title: "Position the Battery",
        instruction:
          "Put the coin battery on the − PAD with the + side up. Tape its edge.",
        image: "/img/electronics-kit/p1_rocket_card_step3.png",
      },
    ],
    proTip:
      "No coin battery? Use the 9V battery from your kit with a resistor: black wire on the − PAD, resistor on the red wire, press its end on the + PAD.",
  },
  {
    id: 2,
    number: "02",
    title: "My First Light",
    time: "5 min",
    difficulty: "Beginner",
    category: "Lights",
    tagline: "Make an LED glow with the 9V battery.",
    description:
      "The foundational electronics circuit. Learn how current travels from the positive battery terminal through a resistor and LED, returning to the negative terminal.",
    kitNeeds: ["9V battery + connector", "Resistor", "Red LED"],
    homeNeeds: ["Tape"],
    youNeedImage: "/img/electronics-kit/p2_my_first_light_you_need.png",
    tryItImage: "/img/electronics-kit/p2_my_first_light_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Attach Resistor to Red Wire",
        instruction:
          "Twist one end of the resistor onto the red wire. Tape it.",
        image: "/img/electronics-kit/p2_my_first_light_step1.png",
      },
      {
        stepNumber: 2,
        title: "Connect LED Long Leg (+)",
        instruction:
          "Twist the LED's LONG leg (+) onto the other end of the resistor. Tape it.",
        image: "/img/electronics-kit/p2_my_first_light_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Black Wire to Short Leg (−)",
        instruction:
          "Twist the black wire onto the LED's SHORT leg (−). Tape it.",
        image: "/img/electronics-kit/p2_my_first_light_step3.png",
      },
    ],
    proTip: "No light? Turn the LED round. Remember: long leg goes to red side, short leg goes to black side.",
  },
  {
    id: 3,
    number: "03",
    title: "Magic Light Switch",
    time: "10 min",
    difficulty: "Beginner",
    category: "Switches",
    tagline: "The light comes on only when you press the button.",
    description:
      "Add a push button to control your circuit! Discover how a mechanical switch breaks and completes the electrical loop.",
    kitNeeds: ["9V battery + connector", "Push button", "Resistor", "LED (Green or Yellow)"],
    homeNeeds: ["Tape"],
    youNeedImage: "/img/electronics-kit/p3_magic_light_switch_you_need.png",
    tryItImage: "/img/electronics-kit/p3_magic_light_switch_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Connect Red Wire",
        instruction:
          "Red wire to one corner of the button.",
        image: "/img/electronics-kit/p3_magic_light_switch_step1.png",
      },
      {
        stepNumber: 2,
        title: "Connect Resistor to Opposite Corner",
        instruction:
          "Join the resistor to the OPPOSITE corner leg of the button.",
        image: "/img/electronics-kit/p3_magic_light_switch_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect LED Long Leg",
        instruction:
          "Join the resistor to the LED's LONG leg (+). Tape it.",
        image: "/img/electronics-kit/p3_magic_light_switch_step3.png",
      },
      {
        stepNumber: 4,
        title: "Connect Black Wire to Short Leg",
        instruction:
          "Join the black wire to the LED's SHORT leg (-). Tape it.",
        image: "/img/electronics-kit/p3_magic_light_switch_step4.png",
      },
    ],
    proTip:
      "When you press the button, it bridges the gap inside and current flows. Let go, and the spring breaks the circuit!",
  },
  {
    id: 4,
    number: "04",
    title: "My Doorbell",
    time: "10 min",
    difficulty: "Beginner",
    category: "Sound",
    tagline: "Press the button to hear a loud beep.",
    description:
      "Replace the silent LED with an audio buzzer to create a working doorbell for your bedroom door!",
    kitNeeds: ["9V battery + connector", "Buzzer", "Push button"],
    homeNeeds: ["Tape", "Cardboard for mounting"],
    youNeedImage: "/img/electronics-kit/p4_my_doorbell_you_need.png",
    tryItImage: "/img/electronics-kit/p4_my_doorbell_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Connect Red Wire",
        instruction:
          "Red wire to one corner of the button.",
        image: "/img/electronics-kit/p4_my_doorbell_step1.png",
      },
      {
        stepNumber: 2,
        title: "Connect Buzzer Long Leg",
        instruction:
          "Join the buzzer's LONG leg (+) to the OPPOSITE corner leg.",
        image: "/img/electronics-kit/p4_my_doorbell_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Black Wire to Short Leg",
        instruction:
          "Join the black wire to the buzzer's SHORT leg (-). Tape it.",
        image: "/img/electronics-kit/p4_my_doorbell_step3.png",
      },
    ],
    proTip: "Buzzers have polarity too! Make sure the buzzer's red wire connects to the button (red side) and black wire connects to black.",
  },
  {
    id: 5,
    number: "05",
    title: "Traffic Signal",
    time: "30 min",
    difficulty: "Intermediate",
    category: "Lights",
    tagline: "Turn a paper clip to pick red, yellow, or green.",
    description:
      "Create a multi-way selector switch using a paper clip! Build a full 3-light traffic signal on cardboard for your toy cars.",
    kitNeeds: ["9V battery + connector", "3 LEDs (Red, Yellow, Green)", "3 Resistors"],
    homeNeeds: ["Cardboard piece", "Paper clip", "Drawing pin", "Tape", "Kitchen foil"],
    youNeedImage: "/img/electronics-kit/p5_traffic_light_you_need.png",
    tryItImage: "/img/electronics-kit/p5_traffic_light_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Make the Switch",
        instruction:
          "Make the switch: stick 3 foil squares on cardboard in a curve. Write R, Y and G on them.",
        image: "/img/electronics-kit/p5_traffic_light_step1.png",
      },
      {
        stepNumber: 2,
        title: "Pin the Paper Clip",
        instruction:
          "Push a drawing pin through one end of a paper clip into the board. Ask a grown-up.",
        image: "/img/electronics-kit/p5_traffic_light_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Red Wire",
        instruction:
          "Tape the red wire under the pin, so it touches the paper clip.",
        image: "/img/electronics-kit/p5_traffic_light_step3.png",
      },
      {
        stepNumber: 4,
        title: "Tape Resistors to Foil",
        instruction:
          "Tape one resistor leg onto each foil square.",
        image: "/img/electronics-kit/p5_traffic_light_step4.png",
      },
      {
        stepNumber: 5,
        title: "Join LEDs to Resistors",
        instruction:
          "Join an LED to each resistor (long leg +). Red on R, yellow on Y, green on G.",
        image: "/img/electronics-kit/p5_traffic_light_step5.png",
      },
      {
        stepNumber: 6,
        title: "Connect Short Legs to Ground",
        instruction:
          "Twist all 3 SHORT legs (-) together with the black wire. Tape it.",
        image: "/img/electronics-kit/p5_traffic_light_step6.png",
      },
    ],
    proTip: "Play traffic police with your toy cars! Use tape on the underside so bare wires don't short out against each other.",
  },
  {
    id: 6,
    number: "06",
    title: "Desk Fan",
    time: "10 min",
    difficulty: "Beginner",
    category: "Motion",
    tagline: "Your own mini fan on a paper cup.",
    description:
      "Turn electrical energy into mechanical movement! Mount the motor onto an upside-down paper cup and feel the breeze.",
    kitNeeds: ["9V battery + connector", "Motor", "Fan blade"],
    homeNeeds: ["Paper cup", "Tape", "Scissors"],
    youNeedImage: "/img/electronics-kit/p6_desk_fan_you_need.png",
    tryItImage: "/img/electronics-kit/p6_desk_fan_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Attach Fan Blade",
        instruction:
          "Push the fan blade firmly onto the motor's thin metal rod.",
        image: "/img/electronics-kit/p6_desk_fan_step1.png",
      },
      {
        stepNumber: 2,
        title: "Mount Motor on Paper Cup",
        instruction:
          "Turn a paper cup upside down. Tape the motor on top. The fan must hang over the edge.",
        image: "/img/electronics-kit/p6_desk_fan_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Red Wire",
        instruction:
          "Join the red wire to one metal tab of the motor. Tape it.",
        image: "/img/electronics-kit/p6_desk_fan_step3.png",
      },
      {
        stepNumber: 4,
        title: "Connect Black Wire",
        instruction:
          "Join the black wire to the other tab. Tape it.",
        image: "/img/electronics-kit/p6_desk_fan_step4.png",
      },
    ],
    proTip:
      "Want the air to blow the other way? Swap the red and black wires between the two motor terminals: the motor reverses direction immediately!",
  },
  {
    id: 7,
    number: "07",
    title: "Morse Code Machine",
    time: "15 min",
    difficulty: "Intermediate",
    category: "Sound",
    tagline: "One press gives a beep and a flash. Send secret messages.",
    description:
      "Build a classic telegraph station! Combining the buzzer and an LED with a push button lets you transmit audio-visual Morse code.",
    kitNeeds: ["9V battery + connector", "Buzzer", "LED (Yellow or Red)", "Resistor", "Push button"],
    homeNeeds: ["Tape", "Cardboard"],
    youNeedImage: "/img/electronics-kit/p7_morse_code_you_need.png",
    tryItImage: "/img/electronics-kit/p7_morse_code_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Connect Red Wire",
        instruction:
          "Join the red wire to one corner leg of the button.",
        image: "/img/electronics-kit/p7_morse_code_step1.png",
      },
      {
        stepNumber: 2,
        title: "Connect Buzzer",
        instruction:
          "On the opposite corner leg, join the buzzer's LONG leg (+).",
        image: "/img/electronics-kit/p7_morse_code_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Resistor & LED",
        instruction:
          "On the SAME button leg, also join a resistor. Then the LED's LONG leg (+).",
        image: "/img/electronics-kit/p7_morse_code_step3.png",
      },
      {
        stepNumber: 4,
        title: "Join Short Legs to Black Wire",
        instruction:
          "Twist both SHORT legs (-) together with the black wire. Tape it.",
        image: "/img/electronics-kit/p7_morse_code_step4.png",
      },
    ],
    proTip:
      "Morse code was used for over a century for global telegraphs and ship-to-shore emergency signals. Use our interactive chart below to spell your name!",
  },
  {
    id: 8,
    number: "08",
    title: "Steady Hand Game",
    time: "25 min",
    difficulty: "Intermediate",
    category: "Games",
    tagline: "Move the ring along the wavy rope without touching it.",
    description:
      "A classic carnival game! Bend a wavy wire track and make a wand with a loop. Touch the wire, and the buzzer sounds the alarm!",
    kitNeeds: ["9V battery + connector", "Buzzer"],
    homeNeeds: ["Paper clips or craft wire", "Cardboard base", "Tape", "Drawing pin"],
    youNeedImage: "/img/electronics-kit/p8_steady_hand_you_need.png",
    tryItImage: "/img/electronics-kit/p8_steady_hand_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Make the Foil Rope",
        instruction:
          "Squeeze and roll a long piece of foil into a rope, as thick as a pencil.",
        image: "/img/electronics-kit/p8_steady_hand_step1.png",
      },
      {
        stepNumber: 2,
        title: "Bend and Stand the Track",
        instruction:
          "Bend it into waves. Stand both ends in clay on cardboard. Tape both ends: START and FINISH.",
        image: "/img/electronics-kit/p8_steady_hand_step2.png",
      },
      {
        stepNumber: 3,
        title: "Make Paper Clip Ring",
        instruction:
          "Open a paper clip. Bend one end into a ring. Hook the ring around the rope.",
        image: "/img/electronics-kit/p8_steady_hand_step3.png",
      },
      {
        stepNumber: 4,
        title: "Connect Buzzer to Battery and Track",
        instruction:
          "Red wire to buzzer LONG leg (+). Buzzer SHORT leg (-) taped to the START end.",
        image: "/img/electronics-kit/p8_steady_hand_step4.png",
      },
      {
        stepNumber: 5,
        title: "Connect Black Wire to Wand",
        instruction:
          "Tape the black wire to the top of the paper clip.",
        image: "/img/electronics-kit/p8_steady_hand_step5.png",
      },
    ],
    proTip:
      "Make it harder by adding sharper zig-zags or making the wand loop smaller. Challenge family members to see who has the steadiest hands!",
  },
  {
    id: 9,
    number: "09",
    title: "Rain Detector",
    time: "20 min",
    difficulty: "Intermediate",
    category: "Sensors",
    tagline: "When water falls on the foil, the LED lights up.",
    description:
      "Water contains minerals that conduct electricity. Build two interleaved foil tracks that stay open until a raindrop bridges them!",
    kitNeeds: ["9V battery + connector", "LED", "Resistor"],
    homeNeeds: ["Kitchen foil", "Cardboard piece", "Glue stick", "Tape", "Water drops"],
    youNeedImage: "/img/electronics-kit/p9_rain_detector_you_need.png",
    tryItImage: "/img/electronics-kit/p9_rain_detector_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Cut Foil Strips",
        instruction:
          "Cut 8 thin strips of foil, as wide as a pencil.",
        image: "/img/electronics-kit/p9_rain_detector_step1.png",
      },
      {
        stepNumber: 2,
        title: "Glue Interlocking Combs",
        instruction:
          "Glue them on a card to make 2 combs. The combs must NOT touch. Leave a thin gap.",
        image: "/img/electronics-kit/p9_rain_detector_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Resistor & LED",
        instruction:
          "Red wire to a resistor. Resistor to the LED's LONG leg (+).",
        image: "/img/electronics-kit/p9_rain_detector_step3.png",
      },
      {
        stepNumber: 4,
        title: "Connect to Comb 1",
        instruction:
          "Tape the LED's SHORT leg (-) onto comb 1.",
        image: "/img/electronics-kit/p9_rain_detector_step4.png",
      },
      {
        stepNumber: 5,
        title: "Connect Black Wire to Comb 2",
        instruction:
          "Tape the black wire onto comb 2.",
        image: "/img/electronics-kit/p9_rain_detector_step5.png",
      },
    ],
    proTip:
      "The glow can be soft, so test in a dim room. A pinch of salt dissolved in the water makes it much brighter! Wipe the card dry to turn it off.",
  },
  {
    id: 10,
    number: "10",
    title: "Step-on Welcome Fan",
    time: "30 min",
    difficulty: "Advanced",
    category: "Sensors",
    tagline: "Hide a secret switch under the doormat. When someone steps on it, the fan turns on.",
    description:
      "Build a pressure-sensitive floor switch! Stepping on the mat pushes two foil sheets together, activating a cooling fan or greeting light.",
    kitNeeds: ["9V battery + connector", "Motor", "Fan blade (or LED + Resistor for Welcome Light)"],
    homeNeeds: ["Two sheets of cardboard", "Kitchen foil", "Tape", "Small bits of foam / spongy tape"],
    youNeedImage: "/img/electronics-kit/p10_welcome_fan_you_need.png",
    tryItImage: "/img/electronics-kit/p10_welcome_fan_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Glue Foil to Cardboard Sheets",
        instruction:
          "Glue foil on one side of 2 cardboard sheets. Let a strip of foil stick out as a tail.",
        image: "/img/electronics-kit/p10_welcome_fan_step1.png",
      },
      {
        stepNumber: 2,
        title: "Make Paper Separator Window",
        instruction:
          "Cut a big window out of a sheet of paper. This keeps the foils apart.",
        image: "/img/electronics-kit/p10_welcome_fan_step2.png",
      },
      {
        stepNumber: 3,
        title: "Stack the Pressure Mat",
        instruction:
          "Stack them: card (foil up), paper frame, card (foil down). Tape the edges.",
        image: "/img/electronics-kit/p10_welcome_fan_step3.png",
      },
      {
        stepNumber: 4,
        title: "Connect Bottom Foil to Motor",
        instruction:
          "Stand the motor and fan on a cup. Tape the bottom foil's tail to one motor tab.",
        image: "/img/electronics-kit/p10_welcome_fan_step4.png",
      },
      {
        stepNumber: 5,
        title: "Connect Battery Wires",
        instruction:
          "Red wire to the top foil's tail. Black wire to the other motor tab.",
        image: "/img/electronics-kit/p10_welcome_fan_step5.png",
      },
    ],
    proTip:
      "Want a welcome light instead of a fan? Simply replace the motor with a resistor and LED! Place it by your bedroom doorway.",
  },
  {
    id: 11,
    number: "11",
    title: "Conductor Detective",
    time: "15 min",
    difficulty: "Beginner",
    category: "Sensors",
    tagline: "Find out which things let electricity pass through.",
    description:
      "Become a science detective! Create open test probes and test household objects to see which are electrical conductors and which are insulators.",
    kitNeeds: ["9V battery + connector", "LED", "Resistor"],
    homeNeeds: ["Tape", "Various household items: Spoon, Coin, Key, Foil, Pencil tip, Eraser, Ruler, Paper"],
    youNeedImage: "/img/electronics-kit/p11_conductor_detective_you_need.png",
    tryItImage: "/img/electronics-kit/p11_conductor_detective_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Join Resistor to LED Long Leg",
        instruction:
          "Join a resistor to the LED's LONG leg (+). Tape it.",
        image: "/img/electronics-kit/p11_conductor_detective_step1.png",
      },
      {
        stepNumber: 2,
        title: "Connect Black Wire to Short Leg",
        instruction:
          "Join the black wire to the LED's SHORT leg (-). Tape it.",
        image: "/img/electronics-kit/p11_conductor_detective_step2.png",
      },
      {
        stepNumber: 3,
        title: "Make Your 2 Probes",
        instruction:
          "Clip the battery. The red wire tip and the resistor's free leg are your 2 probes.",
        image: "/img/electronics-kit/p11_conductor_detective_step3.png",
      },
      {
        stepNumber: 4,
        title: "Test a Conductor (Spoon)",
        instruction:
          "Touch both probes to a spoon. LED glows = it lets electricity pass. CONDUCTOR!",
        image: "/img/electronics-kit/p11_conductor_detective_step4.png",
      },
    ],
    proTip:
      "Most metals are great conductors. Plastic, wood, and rubber are insulators. But look out for graphite in pencil tips—it's carbon and conducts electricity!",
  },
  {
    id: 12,
    number: "12",
    title: "Pencil Dimmer",
    time: "15 min",
    difficulty: "Intermediate",
    category: "Sensors",
    tagline: "A pencil line makes your LED bright or dim.",
    description:
      "Graphite in pencil leads is conductive with natural electrical resistance. Sliding a wire along a dark pencil track acts like a volume knob or light dimmer!",
    kitNeeds: ["9V battery + connector", "LED", "Resistor"],
    homeNeeds: ["Dark pencil (2B, 4B, or HB)", "Plain paper", "Tape"],
    youNeedImage: "/img/electronics-kit/p12_pencil_dimmer_you_need.png",
    tryItImage: "/img/electronics-kit/p12_pencil_dimmer_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Draw a Shiny Graphite Track",
        instruction:
          "Shade a thick, dark pencil line on paper. Go over it again and again until it shines.",
        image: "/img/electronics-kit/p12_pencil_dimmer_step1.png",
      },
      {
        stepNumber: 2,
        title: "Assemble the Probe Circuit",
        instruction:
          "Build the probe circuit from Conductor Detective.",
        image: "/img/electronics-kit/p12_pencil_dimmer_step2.png",
      },
      {
        stepNumber: 3,
        title: "Test Close Probes (Bright)",
        instruction:
          "Put one probe at the start of the line. Put the other probe CLOSE to it: BRIGHT!",
        image: "/img/electronics-kit/p12_pencil_dimmer_step3.png",
      },
    ],
    proTip:
      "The longer the graphite path, the more resistance current faces, and the dimmer the LED gets. You just built a potentiometer (variable resistor)!",
  },
  {
    id: 13,
    number: "13",
    title: "Colour Spinner",
    time: "20 min",
    difficulty: "Beginner",
    category: "Motion",
    tagline: "Spin a rainbow and watch it turn almost white.",
    description:
      "Discover the science of light and optics! When the 7 rainbow colors spin at high speed on the motor, our eyes combine them into white light.",
    kitNeeds: ["9V battery + connector", "Motor"],
    homeNeeds: ["White card circle", "Sketch pens (VIBGYOR)", "Scissors", "Tape / Glue"],
    youNeedImage: "/img/electronics-kit/p13_colour_spinner_you_need.png",
    tryItImage: "/img/electronics-kit/p13_colour_spinner_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Color the Newton Disc",
        instruction:
          "Cut a circle of card. Colour 7 slices: violet, blue, sky blue, green, yellow, orange, red.",
        image: "/img/electronics-kit/p13_colour_spinner_step1.png",
      },
      {
        stepNumber: 2,
        title: "Attach Disc to Fan Blade",
        instruction:
          "Tape the circle flat on the front of the fan blade.",
        image: "/img/electronics-kit/p13_colour_spinner_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Motor Wires",
        instruction:
          "Connect red and black wires to the motor tabs, like the Desk Fan.",
        image: "/img/electronics-kit/p13_colour_spinner_step3.png",
      },
    ],
    proTip:
      "Sir Isaac Newton proved in 1666 that white sunlight is composed of all the colors of the rainbow combined together.",
  },
  {
    id: 14,
    number: "14",
    title: "Drawing Robot (Scribble-Bot)",
    time: "30 min",
    difficulty: "Advanced",
    category: "Motion",
    tagline: "A wobbly paper-cup robot that draws on its own.",
    description:
      "Build a self-propelled art bot! Sticking an off-center lump of clay to the motor shaft causes eccentric vibrations that make the cup dance across paper, drawing wild spiral patterns.",
    kitNeeds: ["9V battery + connector", "Motor"],
    homeNeeds: ["Paper cup", "3 Sketch pens / markers", "Clay / plasticine", "Tape", "Large sheet of paper"],
    youNeedImage: "/img/electronics-kit/p14_drawing_robot_you_need.png",
    tryItImage: "/img/electronics-kit/p14_drawing_robot_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Attach Sketch Pen Legs",
        instruction:
          "Turn a paper cup upside down. Tape 3 sketch pens around it. Caps off, tips down.",
        image: "/img/electronics-kit/p14_drawing_robot_step1.png",
      },
      {
        stepNumber: 2,
        title: "Add Clay Counterweight to Blade",
        instruction:
          "Push the fan blade on the motor. Stick a lump of clay on ONE blade only.",
        image: "/img/electronics-kit/p14_drawing_robot_step2.png",
      },
      {
        stepNumber: 3,
        title: "Mount Motor on Cup",
        instruction:
          "Tape the motor flat on top of the cup. The fan must hang over the side.",
        image: "/img/electronics-kit/p14_drawing_robot_step3.png",
      },
      {
        stepNumber: 4,
        title: "Wire Battery to Motor",
        instruction:
          "Tape the battery on the cup. Join the red and black wires to the motor tabs.",
        image: "/img/electronics-kit/p14_drawing_robot_step4.png",
      },
    ],
    proTip:
      "Not moving much? Add a little more clay to increase vibration. Moving too fast or falling over? Use slightly less clay or spread the pen legs wider.",
  },
  {
    id: 15,
    number: "15",
    title: "Door Alarm",
    time: "25 min",
    difficulty: "Advanced",
    category: "Sensors",
    tagline: "A clothes peg alarm that buzzes when someone opens your door.",
    description:
      "Protect your room from sneaky intruders! A wooden clothes peg wrapped in foil is held open by a cardboard insulator tied to your door. Opening the door pulls the tab, snapping the peg shut and sounding the alarm!",
    kitNeeds: ["9V battery + connector", "Buzzer"],
    homeNeeds: ["Wooden clothes peg", "Kitchen foil", "Tape", "Cardboard tab", "Thread / string", "Door"],
    youNeedImage: "/img/electronics-kit/p15_door_alarm_you_need.png",
    tryItImage: "/img/electronics-kit/p15_door_alarm_try_it.png",
    steps: [
      {
        stepNumber: 1,
        title: "Wrap Foil Around Peg Jaws",
        instruction:
          "Wrap foil around the tips of both jaws of a clothes peg.",
        image: "/img/electronics-kit/p15_door_alarm_step1.png",
      },
      {
        stepNumber: 2,
        title: "Connect Buzzer to Battery and Top Jaw",
        instruction:
          "Red wire to buzzer LONG leg (+). Buzzer SHORT leg (-) to the TOP foil.",
        image: "/img/electronics-kit/p15_door_alarm_step2.png",
      },
      {
        stepNumber: 3,
        title: "Connect Black Wire to Bottom Jaw",
        instruction:
          "Tape the black wire to the BOTTOM foil.",
        image: "/img/electronics-kit/p15_door_alarm_step3.png",
      },
      {
        stepNumber: 4,
        title: "Test Closed Peg Buzzer",
        instruction:
          "Clip the battery. The jaws touch, so it buzzes. It works!",
        image: "/img/electronics-kit/p15_door_alarm_step4.png",
      },
      {
        stepNumber: 5,
        title: "Insert Card Tab & Tie Thread to Door",
        instruction:
          "Slide a card between the jaws: quiet. Tie a thread from the card to the door handle.",
        image: "/img/electronics-kit/p15_door_alarm_step5.png",
      },
    ],
    proTip:
      "This is how real tripwire security systems work! Test the length of the string so opening the door just 2 inches immediately trips the alarm.",
  },
];

export const morseCodeTable = [
  { letter: "A", code: "·–" },
  { letter: "B", code: "–···" },
  { letter: "C", code: "–·–·" },
  { letter: "D", code: "–··" },
  { letter: "E", code: "·" },
  { letter: "F", code: "··–·" },
  { letter: "G", code: "––·" },
  { letter: "H", code: "····" },
  { letter: "I", code: "··" },
  { letter: "J", code: "·–––" },
  { letter: "K", code: "–·–" },
  { letter: "L", code: "·–··" },
  { letter: "M", code: "––" },
  { letter: "N", code: "–·" },
  { letter: "O", code: "–––" },
  { letter: "P", code: "·––·" },
  { letter: "Q", code: "––·–" },
  { letter: "R", code: "·–·" },
  { letter: "S", code: "···" },
  { letter: "T", code: "–" },
  { letter: "U", code: "··–" },
  { letter: "V", code: "···–" },
  { letter: "W", code: "·––" },
  { letter: "X", code: "–··–" },
  { letter: "Y", code: "–·––" },
  { letter: "Z", code: "––··" },
];

export const conductorDetectiveData = [
  { item: "Metal Spoon", material: "Stainless Steel", conducts: true, explanation: "Metals have free electrons that carry electricity easily." },
  { item: "Metal Coin", material: "Copper / Nickel", conducts: true, explanation: "Coin metals are outstanding electrical conductors." },
  { item: "House Key", material: "Brass / Steel", conducts: true, explanation: "Keys are solid metal and readily complete the circuit." },
  { item: "Kitchen Foil", material: "Aluminum", conducts: true, explanation: "Aluminum is the same conductor used in household foil circuits." },
  { item: "Pencil Tip", material: "Graphite (Carbon)", conducts: true, explanation: "Graphite is a non-metal conductor with layered carbon atoms!" },
  { item: "Rubber Eraser", material: "Synthetic Rubber", conducts: false, explanation: "Rubber is a strong insulator; electrons cannot jump through it." },
  { item: "Plastic Ruler", material: "Acrylic / Plastic", conducts: false, explanation: "Plastic holds tightly to its electrons, stopping current flow." },
  { item: "Piece of Paper", material: "Wood Pulp / Cellulose", conducts: false, explanation: "Dry paper is a natural electrical insulator." },
];

export const troubleshootingItems = [
  {
    problem: "Nothing works",
    cause: "Loose connection or unclipped battery.",
    solution: "Check that the 9V battery is clipped on tightly and every joint is bare metal touching bare metal. Tape holds, but metal connects.",
  },
  {
    problem: "LED does not light",
    cause: "LED is inserted backwards.",
    solution: "Turn the LED round. Remember: the LONG leg is positive (+) and must connect to the red wire side; the SHORT leg is negative (−).",
  },
  {
    problem: "LED stopped working",
    cause: "Connected directly to 9V without a resistor.",
    solution: "The LED burnt out from too much current. Replace with another LED from your kit, and ALWAYS connect a resistor in line on 9V.",
  },
  {
    problem: "Buzzer is quiet or silent",
    cause: "Buzzer polarity is reversed.",
    solution: "Turn the buzzer round: red wire goes towards positive, black wire goes to negative.",
  },
  {
    problem: "Button does nothing",
    cause: "Wires connected to pins on the same internal track.",
    solution: "Use the two legs at opposite diagonal corners (one top, one bottom, on different sides). Opposite corners work every time.",
  },
  {
    problem: "Motor hums but does not spin",
    cause: "Slight friction or fan hitting surface.",
    solution: "Give the fan blade a small flick with your finger. Check that the blade isn't rubbing against the tape or cup rim.",
  },
  {
    problem: "Battery feels warm",
    cause: "Short circuit (red and black wires touching directly).",
    solution: "Unclip the battery immediately! Inspect your wires to ensure red and black bare metal parts never touch each other directly.",
  },
];
